import { addProduct, selectGiftProduct } from '@/fetchers/cart';
import { getToken } from '@/helpers/auth';
import { getLangCountry } from '@/helpers/languages';
import { useRouter } from 'next/router';
import React, { Fragment, useMemo, useRef, useState } from 'react';
import { useProductState } from '../store';
import { useSelector, useStore } from 'react-redux';
import { SET_SESSION_DATA } from '@/context/types';
import { IGlobalState } from '@/context/reducer';
import { useAlert } from '@/context/AlertProvider';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/UI/Button';
import { useIntersectionObserver } from 'usehooks-ts';
import { Portal, Transition } from '@headlessui/react';
import Image from 'next/future/image';
import { find, first, flatten, isEmpty } from 'lodash';
import Price from './Price';
import { scrollToId } from '@/helpers/scroller';
import { fetchShippingCountriesList } from '@/fetchers/shipping';

import useQueryParams from '@/hooks/useQueryParams';
import { useGiftProduct } from '@/hooks/products/use-gift-product';

const AddToCart = () => {
  const token = getToken();
  const alert = useAlert();
  const store = useStore();
  const router = useRouter();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [query] = useQueryParams();

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const mainButtonEntry = useIntersectionObserver(buttonRef, {});
  const isMainButtonVisible = mainButtonEntry?.isIntersecting ?? true;

  const { product, variation, color } = useProductState();
  const { session } = useSelector<IGlobalState, IGlobalState>(
    state => state || {}
  );

  const gift = useGiftProduct(product.selling_code);

  async function addToCart() {
    setLoading(true);

    const { cart, coupons } = await addProduct({
      ...getLangCountry(router.query),
      token,
      sku: variation!.sku,
      quantity: 1
    });

    setLoading(false);

    store.dispatch({
      type: SET_SESSION_DATA,
      payload: { ...session, cart, coupons }
    });

    document.querySelector<HTMLButtonElement>('#menu-cart')?.click();
    alert.show(t('PRODUCT_ADDED_TO_CART'));

    if (window.dataLayer) {
      const { lang, country } = getLangCountry(router.query);

      const countries = fetchShippingCountriesList({ lang });
      const currentCountry = countries.find(c => {
        return c['country-code']?.toLowerCase() === country.toLowerCase();
      });
      let value = variation?.price?.sale_price || variation?.price?.price || 0;
      let discount = 0;
      if (
        variation?.price?.sale_price &&
        variation?.price?.price &&
        Number.parseFloat(variation.price?.price) > 0 &&
        Number.parseFloat(variation.price?.sale_price) > 0
      ) {
        discount =
          Number.parseFloat(variation.price?.price) -
          Number.parseFloat(variation.price?.sale_price);
      }
      const category =
        product.breadcrumbs[product.breadcrumbs.length - 1] || {};

      window.dataLayer?.push({ ecommerce: null });
      window.dataLayer?.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currentCountry?.currency || 'EUR',
          value,
          items: [
            {
              item_id: product.product_type + '-' + product.selling_code,
              item_name: product.productName,
              discount,
              index: 0,
              item_brand: 'Uyn',
              item_category: category.slug,
              item_list_id: 'single_product_page',
              item_list_name: 'Single product page',
              item_variant: variation!.sku,
              price: value,
              quantity: 1
            }
          ]
        }
      });
    }
  }

  async function addGiftToCart() {
    try {
      setLoading(true);

      const { cart, coupons } = await selectGiftProduct({
        ...getLangCountry(query),
        token,
        sku: variation!.sku,
        promotion_id: Number.parseInt(gift?.promotion || '0')
      });

      store.dispatch({
        type: SET_SESSION_DATA,
        payload: { ...session, cart, coupons }
      });
      // Simulo il click sul pulsante per aprire il carrello
      document.querySelector<HTMLButtonElement>('#menu-cart')?.click();
      alert.show(t('PRODUCT_ADDED_TO_CART'));
    } catch (error) {
      alert.error(t('GENERIC_ERROR'));
    } finally {
      setLoading(false);
    }
  }

  const firstVariationImage = useMemo(() => {
    return first(
      flatten(
        find(product.colors, ({ color_code }) => color_code === color)?.images
      )
    );
  }, [product, color]);

  const isInStock =
    variation &&
    Number(variation.stock?.in_stock) === 1 &&
    Number(variation.stock?.stock_quantity) > 0;

  return (
    <Fragment>
      <Button
        id="add-to-cart"
        ref={buttonRef}
        variant="dark-big"
        className="w-full"
        loading={loading}
        onClick={gift ? addGiftToCart : addToCart}
        disabled={!isInStock || loading}
      >
        {t(gift ? 'CHOOSE_GIFT_PRODUCT' : 'P_PDP.OVERVIEW.ADD_TO_CART')}
      </Button>

      <Portal>
        <Transition
          as="div"
          show={!isMainButtonVisible}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
          className="fixed z-10 inset-x-0 bottom-0 p-3 bg-white md:hidden"
        >
          <Button
            variant="dark-big"
            className="w-full"
            loading={loading}
            onClick={() => scrollToId('product-options')}
          >
            {t(gift ? 'CHOOSE_GIFT_PRODUCT' : 'P_PDP.OVERVIEW.ADD_TO_CART')}
          </Button>
        </Transition>
      </Portal>

      <Portal>
        <Transition
          as="div"
          show={!isMainButtonVisible && Boolean(firstVariationImage)}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
          className="hidden fixed z-10 right-6 bottom-6 bg-white shadow p-3 md:flex md:items-center md:space-x-3"
        >
          <img
            alt={variation?.label_size || ''}
            src={firstVariationImage!}
            className="h-16 w-auto object-contain"
          />
          {/* <Image
            alt={variation?.label_size || ''}
            src={firstVariationImage!}
            width={0}
            height={0}
            sizes="100vw"
            className="h-16 w-auto object-contain"
          /> */}

          <div className="space-y-1.5">
            <h3 className="font-bold uppercase">{product.productName}</h3>
            <Price />
          </div>

          <Button
            variant="dark-big"
            loading={loading}
            onClick={() => scrollToId('product-options')}
          >
            {t(gift ? 'CHOOSE_GIFT_PRODUCT' : 'P_PDP.OVERVIEW.ADD_TO_CART')}
          </Button>
        </Transition>
      </Portal>
    </Fragment>
  );
};

export default AddToCart;
